// Shared Sentry wiring for apps that embed as <script>-loaded Web Components
// into host sites that may also be running Sentry. Follows Sentry's
// "shared environments" best-practice: a manually-built BrowserClient on a
// dedicated Scope that never touches globals the host owns.
//
// https://docs.sentry.io/platforms/javascript/guides/react/best-practices/shared-environments/

import { BrowserClient, defaultStackParser, getDefaultIntegrations, makeFetchTransport, Scope } from '@sentry/react'
import type { ErrorEvent, EventHint, SeverityLevel } from '@sentry/react'
import { Component, createElement } from 'react'
import type { ComponentType, ErrorInfo, ReactNode } from 'react'

// Passed to `sentryVitePlugin({ applicationKey })` by each app's Vite config.
// Must match the host site's `thirdPartyErrorFilterIntegration({ filterKeys })`
// entry so our bundle's stack frames are treated as first-party.
export const APPLICATION_KEY = 'understood-web'

export interface SentryEmbedOptions {
  /** Short identifier for this embed, e.g. 'tme' or 'companion'. Tagged on every event. */
  appName: string
  dsn: string
  environment: string
  /** Must match the release name passed to `sentryVitePlugin` so source maps resolve. */
  release?: string
  /** Pass `document.currentScript?.src` from the embed's entry module (read at top-level). */
  scriptUrl?: string
  debug?: boolean
  beforeSend?: (event: ErrorEvent, hint: EventHint) => ErrorEvent | null
}

export interface ErrorBoundaryFallbackRenderProps {
  error: unknown
  componentStack: string
  eventId: string
  resetError: () => void
}

export type ErrorBoundaryFallback = ReactNode | ((props: ErrorBoundaryFallbackRenderProps) => ReactNode)

export interface ErrorBoundaryProps {
  children: ReactNode
  fallback: ErrorBoundaryFallback
  onError?: (error: unknown, componentStack: string, eventId: string) => void
}

interface ErrorBoundaryState {
  error: unknown
  componentStack: string | null
  eventId: string | null
}

export interface SentryEmbed {
  scope: Scope
  ErrorBoundary: ComponentType<ErrorBoundaryProps>
  captureException: (error: unknown, hint?: EventHint) => string
  captureMessage: (message: string, level?: SeverityLevel, hint?: EventHint) => string
  withScope: <T>(callback: (scope: Scope) => T) => T
  /**
   * Report a caught error from an async/Promise path. Benign aborts (unmount,
   * AbortController) are ignored. Extra `context` is attached under the `report`
   * context key. Use this in `.catch` handlers instead of bare `console.error`.
   */
  reportError: (error: unknown, context?: Record<string, unknown>) => void
}

// Mirror of tme's isAbortError (apps/tme/src/webgl/core/AbortSignalUtils.ts) so
// the package stays dependency-free. Aborts are expected on unmount/navigation.
function isAbortError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const { name, message } = error as { name?: unknown; message?: unknown }
  return name === 'AbortError' || message === 'Aborted'
}

const EXCLUDED_INTEGRATIONS = ['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers']

export function createSentryEmbed(options: SentryEmbedOptions): SentryEmbed {
  const { appName, dsn, environment, release, scriptUrl, debug = false, beforeSend } = options

  const integrations = getDefaultIntegrations({}).filter(
    (integration) => !EXCLUDED_INTEGRATIONS.includes(integration.name)
  )

  const client = new BrowserClient({
    dsn,
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    integrations,
    environment,
    release,
    debug,
    beforeSend: (event, hint) => (beforeSend ? beforeSend(event, hint) : event),
  })

  const scope = new Scope()
  scope.setClient(client)
  scope.setTag('embed.app', appName)
  scope.setTag('embed.embedded', 'true')
  scope.setContext('embed', {
    scriptUrl: scriptUrl ?? null,
    hostUrl: typeof location !== 'undefined' ? location.href : null,
    hostOrigin: typeof location !== 'undefined' ? location.origin : null,
  })

  client.init()

  const ErrorBoundary = createErrorBoundary(appName, scope)

  return {
    scope,
    ErrorBoundary,
    captureException: (error, hint) => scope.captureException(error, hint),
    captureMessage: (message, level, hint) => scope.captureMessage(message, level, hint),
    // Fork a child scope so tag/user/context mutations inside the callback don't
    // leak back onto the embed's persistent scope (matches stock Sentry.withScope).
    withScope: (callback) => callback(scope.clone()),
    reportError: (error, context) => {
      if (isAbortError(error)) return
      const child = scope.clone()
      if (context) child.setContext('report', context)
      child.captureException(error)
    },
  }
}

function createErrorBoundary(appName: string, scope: Scope): ComponentType<ErrorBoundaryProps> {
  class SentryEmbedErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null, componentStack: null, eventId: null }

    static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
      return { error, componentStack: null, eventId: null }
    }

    componentDidCatch(error: unknown, info: ErrorInfo): void {
      const componentStack = info.componentStack ?? ''
      const eventId = scope.captureException(error, {
        captureContext: { contexts: { react: { componentStack } } },
      })
      this.setState({ componentStack, eventId })
      this.props.onError?.(error, componentStack, eventId)
    }

    resetError = (): void => {
      this.setState({ error: null, componentStack: null, eventId: null })
    }

    render(): ReactNode {
      const { error, componentStack, eventId } = this.state
      const { fallback, children } = this.props

      if (error === null || error === undefined) {
        return children
      }

      if (typeof fallback === 'function') {
        return fallback({
          error,
          componentStack: componentStack ?? '',
          eventId: eventId ?? '',
          resetError: this.resetError,
        })
      }

      return fallback
    }
  }

  // Named for the React DevTools tree and Sentry's React component annotation.
  Object.defineProperty(SentryEmbedErrorBoundary, 'name', {
    value: `SentryEmbedErrorBoundary(${appName})`,
  })

  // Wrap in a functional component to get a stable ComponentType with clean generics,
  // then forward props through createElement so we don't need a JSX runtime here.
  const Wrapped: ComponentType<ErrorBoundaryProps> = (props) => createElement(SentryEmbedErrorBoundary, props)
  Wrapped.displayName = `SentryEmbedErrorBoundary(${appName})`
  return Wrapped
}
